import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuthenticationService } from '../../_services/authentication/authentication.service';
import { MenuItem, MessageService } from 'primeng/api';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { PasswordModule } from 'primeng/password';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { MenuModule } from 'primeng/menu';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastModule } from 'primeng/toast';
import { SessionService } from '../../_services/session/session.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { environment } from '../../../environments/environment';

declare global {
  interface Window {
    google?: any;
  }
}

@Component({
  standalone: true,
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  imports: [
    CommonModule,
    RouterLink,
    ToastModule,
    TranslatePipe,
    FormsModule,
    ButtonModule,
    PasswordModule,
    InputTextModule,
    CheckboxModule,
    MenuModule,
  ],
})
export class LoginComponent implements OnInit, AfterViewInit {
  public accessMode: 'professional' | 'patient' = 'professional';
  public username: string;
  public password: string;
  public languageItems: MenuItem[] = [];
  public currentLang: string = 'EN';
  public googleAvailable = false;
  public googleLoading = false;
  public googleConfigured = false;
  private readonly returnUrl: string | null;
  public currentYear = new Date().getFullYear();

  @ViewChild('googleButtonContainer')
  googleButtonContainer?: ElementRef<HTMLDivElement>;

  constructor(
    private authService: AuthenticationService,
    private messageService: MessageService,
    private translateService: TranslateService,
    private session: SessionService,
    private router: Router,
    private route: ActivatedRoute,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
  ) {
    this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
  }

  ngOnInit() {
    this.syncAccessModeFromRoute();
    this.currentLang = this.translateService.currentLang?.toUpperCase() || 'EN';
    this.setLanguages();

    this.translateService.onLangChange.subscribe(() => {
      this.currentLang = this.translateService.currentLang.toUpperCase();
    });

    this.googleConfigured = !!`${environment.googleClientId || ''}`.trim();
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => this.initializeGoogleLogin());
  }

  private setLanguages() {
    this.languageItems = [
      {
        label: 'Español',
        command: () => this.session.setLanguage('es'),
      },
      {
        label: 'English',
        command: () => this.session.setLanguage('en'),
      },
      {
        label: 'Català',
        command: () => this.session.setLanguage('ca'),
      },
    ];
  }

  public onKeyDown($event: KeyboardEvent) {
    this.login();
  }

  public selectAccessMode(mode: 'professional' | 'patient') {
    this.accessMode = 'professional';
    const invitationReturnUrl = this.normalizeInvitationReturnUrl(this.returnUrl);
    this.router.navigate(['/professionals'], {
      queryParams: invitationReturnUrl
        ? { returnUrl: invitationReturnUrl }
        : undefined,
    });
  }

  public login() {
    // Validate empty fields
    if (!this.username || !this.password) {
      this.translateService
        .get(['LOGIN.ERRORS.TITLE', 'LOGIN.ERRORS.EMPTY_FIELDS'])
        .subscribe((translations) => {
          this.messageService.add({
            severity: 'warn',
            summary: translations['LOGIN.ERRORS.TITLE'],
            detail: translations['LOGIN.ERRORS.EMPTY_FIELDS'],
            life: 5000,
          });
        });
      return;
    }

    this.authService.login(this.username, this.password, this.accessMode).subscribe({
      next: () => {
        this.navigateAfterLogin();
      },
      error: (err: HttpErrorResponse) => {
        this.handleLoginError(err);
      },
    });
  }

  private navigateAfterLogin() {
    const invitationReturnUrl = this.normalizeInvitationReturnUrl(this.returnUrl);
    if (invitationReturnUrl) {
      this.router.navigateByUrl(invitationReturnUrl);
      return;
    }

    this.router.navigate(['/entry']);
  }

  private normalizeInvitationReturnUrl(value: string | null): string | null {
    const returnUrl = `${value || ''}`.trim();
    if (!returnUrl || !returnUrl.startsWith('/') || returnUrl.startsWith('//')) {
      return null;
    }

    const path = returnUrl.split('?')[0].split('#')[0];
    if (path.startsWith('/invitation/') || path.startsWith('/onboarding/')) {
      return returnUrl;
    }

    return null;
  }

  private syncAccessModeFromRoute() {
    this.accessMode = 'professional';
  }

  private initializeGoogleLogin(): void {
    const clientId = `${environment.googleClientId || ''}`.trim();
    if (!clientId || !this.googleButtonContainer?.nativeElement) {
      this.googleAvailable = false;
      this.googleLoading = false;
      this.cdr.detectChanges();
      return;
    }

    this.googleLoading = true;
    this.loadGoogleScript()
      .then(() => {
        const google = window.google;
        if (!google?.accounts?.id) {
          this.googleAvailable = false;
          this.googleLoading = false;
          return;
        }

        google.accounts.id.initialize({
          client_id: clientId,
          callback: (response: { credential?: string }) =>
            this.onGoogleCredential(response?.credential),
          cancel_on_tap_outside: true,
        });

        const googleButtonWidth = Math.min(
          360,
          Math.max(220, this.googleButtonContainer.nativeElement.clientWidth),
        );

        google.accounts.id.renderButton(this.googleButtonContainer.nativeElement, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          shape: 'rectangular',
          text: 'continue_with',
          width: googleButtonWidth,
        });
        this.googleAvailable = true;
        this.googleLoading = false;
        this.cdr.detectChanges();
      })
      .catch(() => {
        this.googleAvailable = false;
        this.googleLoading = false;
        this.cdr.detectChanges();
      });
  }

  private onGoogleCredential(idToken?: string) {
    if (!idToken) {
      return;
    }

    this.authService
      .loginWithGoogle(
        idToken,
        this.translateService.currentLang || 'en',
        this.accessMode,
      )
      .subscribe({
        next: () => {
          this.zone.run(() => this.navigateAfterLogin());
        },
        error: (err: HttpErrorResponse) => {
          this.zone.run(() => this.handleLoginError(err));
        },
      });
  }

  private loadGoogleScript(): Promise<void> {
    if (window.google?.accounts?.id) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const existing = document.querySelector(
        'script[data-vc-google-login="true"]',
      ) as HTMLScriptElement | null;
      if (existing) {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.dataset['vcGoogleLogin'] = 'true';
      script.onload = () => resolve();
      script.onerror = () => reject();
      document.head.appendChild(script);
    });
  }

  private handleLoginError(err: HttpErrorResponse) {
    let errorKey = 'LOGIN.ERRORS.UNKNOWN';
    const backendError = `${err?.error?.err || ''}`.trim();

    if (backendError === 'LOGIN_METHOD_NOT_AVAILABLE') {
      errorKey = 'LOGIN.ERRORS.LOGIN_METHOD_NOT_AVAILABLE';
    } else if (backendError === 'PROFESSIONAL_ACCESS_REQUIRED') {
      errorKey = 'LOGIN.ERRORS.PROFESSIONAL_ACCESS_REQUIRED';
    }

    // Determine error type based on status code
    if (
      errorKey === 'LOGIN.ERRORS.UNKNOWN' &&
      (err.status === 401 || err.statusText === 'Unauthorized')
    ) {
      errorKey = 'LOGIN.ERRORS.INVALID_CREDENTIALS';
    } else if (errorKey === 'LOGIN.ERRORS.UNKNOWN' && err.status === 0) {
      // Network error - server unreachable
      errorKey = 'LOGIN.ERRORS.CONNECTION_ERROR';
    } else if (
      errorKey === 'LOGIN.ERRORS.UNKNOWN' &&
      (err.status === 503 || err.status === 502 || err.status === 504)
    ) {
      // Service unavailable, bad gateway, gateway timeout
      errorKey = 'LOGIN.ERRORS.SERVICE_UNAVAILABLE';
    } else if (errorKey === 'LOGIN.ERRORS.UNKNOWN' && err.status >= 500) {
      // Other server errors
      errorKey = 'LOGIN.ERRORS.SERVICE_UNAVAILABLE';
    }

    this.translateService
      .get(['LOGIN.ERRORS.TITLE', errorKey])
      .subscribe((translations) => {
        this.messageService.add({
          severity: 'error',
          summary: translations['LOGIN.ERRORS.TITLE'],
          detail: translations[errorKey],
          life: 6000,
        });
      });
  }
}
